import {
  BODY_SLOTS,
  ClassIdSchema,
  CLASSES,
  effectiveDps,
  equipmentBonus,
  getItem,
  type Inspection,
  isCosmeticSlot,
  lookTints,
  RARITY_COLORS,
  statsFor,
  wornPieces,
} from '@shefira/shared';

import { type CssAtlas, loadCharacterAtlas } from '../assets/dom-atlas.js';
import { combatOfEquipped } from './combat-lines.js';
import { DollPreview } from './doll-preview.js';
import { closeButton } from './hud.js';
import { describeOnHover, hideItemHover } from './item-hover.js';
import { addLevelBadge, paintItemIcon } from './item-icon.js';
import { formatDps, SLOT_LABELS } from './item-tooltip.js';
import { element, kitHeading, kitSeal, kitStage } from './kit.js';
import { formatCount } from './profile-format.js';
import { hint, statCards } from './profile-sections.js';
import { uiFormat, uiText } from './text.js';
import './inventory.css';
import './profile.css';
import './inspect.css';

/** The card judges nobody: the reader is not the wearer, nothing shows in red. */
const READER = { level: Number.MAX_SAFE_INTEGER, classId: 'warrior' } as const;

/**
 * Another player, inspected (ADR 0085): dressed as the zone shows them,
 * their three stats and every piece they wear, each with its card on hover
 * or tap. The profile's window and pieces, so the two read alike; the stats
 * are the same derivation the profile makes, from what the server sent.
 */
export class InspectPanel {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(private readonly icons: () => CssAtlas | null) {
    this.root = element('section', 'profile inspect');
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    const frame = element('div', 'profile__window');
    this.body = element('div', 'profile__tab-body');
    frame.append(
      this.body,
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );
    this.root.append(frame);
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.close();
    });
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKeyDown);
  }

  show(inspection: Inspection): void {
    this.root.setAttribute(
      'aria-label',
      uiFormat('Équipement de {name}', { name: inspection.name }),
    );
    this.render(inspection);
    this.root.hidden = false;
  }

  close(): void {
    hideItemHover();
    this.root.hidden = true;
  }

  private render(inspection: Inspection): void {
    const { equipped } = inspection;
    const classId = ClassIdSchema.safeParse(inspection.classId);
    const definition = classId.success ? CLASSES[classId.data] : undefined;

    const hero = element('div', 'profile__hero');
    const stage = kitStage('profile__stage');
    if (classId.success) {
      void loadCharacterAtlas().then((atlas) => {
        const prefix = atlas?.prefixOf(`body.${classId.data}`) ?? null;
        if (atlas === null || prefix === null) return;
        const doll = new DollPreview({
          bodyKey: prefix,
          frame: atlas.frame,
          featureTints: lookTints(inspection.look),
          hairStyle: inspection.look.hairStyle,
          body: inspection.look.body,
          scale: 4,
        });
        doll.draw(equipped);
        stage.append(doll.canvas);
      });
    }
    const identity = element('div', 'profile__identity');
    const title = element('div', 'profile__title');
    const names = element('div', 'profile__names');
    names.append(
      element('h2', 'profile__name', inspection.name),
      element('p', 'profile__class', definition?.name ?? inspection.classId),
    );
    title.append(kitSeal(inspection.level), names);
    identity.append(title);
    if (definition !== undefined) {
      const stats = statsFor(
        definition,
        inspection.level,
        equipmentBonus(wornPieces(equipped, getItem)),
      );
      identity.append(
        statCards([
          {
            tone: 'health',
            icon: 'stat-health',
            label: 'Vie',
            value: formatCount(stats.maxHealth),
          },
          {
            tone: 'dps',
            icon: 'stat-dps',
            label: 'DPS',
            value: formatDps(effectiveDps(stats.dps, combatOfEquipped(equipped))),
          },
          {
            tone: 'defence',
            icon: 'stat-defence',
            label: 'Défense',
            value: formatCount(stats.defence),
          },
        ]),
      );
    }
    hero.append(stage, identity);

    const section = element('section', 'profile__section');
    section.append(kitHeading('Équipement'));
    const list = element('ul', 'inspect__pieces');
    for (const slot of BODY_SLOTS) {
      const instance = equipped[slot];
      const template = instance === undefined ? undefined : getItem(instance.templateId);
      if (instance === undefined || template === undefined) continue;
      const row = element('li', 'inspect__piece');
      const slotCase = element('div', 'slot');
      const icon = element('div', `item item--${template.rarity}`);
      paintItemIcon(icon, template, this.icons(), instance.templateId);
      addLevelBadge(icon, instance.upgradeLevel);
      slotCase.append(icon);
      const text = element('div', 'inspect__piece-text');
      const name = element('span', 'inspect__piece-name', template.name);
      name.style.color = `#${RARITY_COLORS[template.rarity].toString(16).padStart(6, '0')}`;
      const label = template.slot === undefined ? '' : uiText(SLOT_LABELS[template.slot]);
      text.append(
        name,
        element(
          'span',
          'inspect__piece-slot',
          isCosmeticSlot(slot) ? uiFormat('{slot} (apparence)', { slot: label }) : label,
        ),
      );
      row.append(slotCase, text);
      describeOnHover(row, template, () => ({ wearer: READER, details: {}, instance }));
      list.append(row);
    }
    section.append(list.childElementCount > 0 ? list : hint('Ne porte rien.'));

    this.body.replaceChildren(hero, section);
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    hideItemHover();
    this.root.remove();
  }
}
